import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import ScheduleIcon from "@mui/icons-material/Schedule";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { SLA_COLORS } from "../../shared/ui/charts";
import { businessMinutesBetween, formatMinutes, PackageSla, SlaColor, slaColor, SlaStage, STAGE_LABEL } from "./sla";

/** Bloco `sla` da listagem (BE-1g). `businessElapsedMin` é opcional: sem ele, o FE calcula o corte útil. */
export type SlaBadgeValue = PackageSla & { businessElapsedMin?: number | null };

// Texto escuro do mockup G1 (.sla-ok/.sla-at/.sla-ko): o tom da paleta sobre o fundo claro não passa em contraste.
const TEXT: Record<SlaColor, string> = { green: "#1b5e20", amber: "#8a5300", red: "#b3261e" };
const COLORS: SlaColor[] = ["green", "amber", "red"];

const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

/** `registrationPause`: chave slaRegistrationPause (conversão do ME) — o relógio da empresa desconta as pausas. */
export default function SlaBadge({ sla, now, registrationPause = false }: { sla?: SlaBadgeValue | null; now?: Date; registrationPause?: boolean }) {
  // Campo ausente ou incompleto (BE sem o BE-1g, pacote fechado) = não mostra.
  if (!sla || !isNum(sla.elapsedMin) || !isNum(sla.targetMin) || !isNum(sla.challengeMin)) {
    return (
      <Box component="span" sx={{ color: "text.secondary" }}>
        —
      </Box>
    );
  }
  const color: SlaColor = COLORS.includes(sla.color) ? sla.color : slaColor(sla.elapsedMin, sla.challengeMin, sla.targetMin);
  const business = isNum(sla.businessElapsedMin)
    ? sla.businessElapsedMin
    : sla.clockStartedAt
      ? businessMinutesBetween(sla.clockStartedAt, now ?? new Date())
      : null;
  const stageLabel = STAGE_LABEL[sla.stage as SlaStage] ?? String(sla.stage);
  const title = (
    <Box sx={{ py: 0.25 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
        {`Etapa: ${stageLabel} · meta ${formatMinutes(sla.targetMin)} · meta desafio ${formatMinutes(sla.challengeMin)}`}
      </Typography>
      <Typography sx={{ fontSize: 12 }}>
        {registrationPause ? "Horas corridas; períodos pausados por registro pendente no ME não contam" : "Horas corridas, sem pausa"}
      </Typography>
      {business !== null && <Typography sx={{ fontSize: 12 }}>{`No corte útil (seg–sex 08–18h): ${formatMinutes(business)}`}</Typography>}
    </Box>
  );
  return (
    <Tooltip title={title} placement="top">
      <Chip
        size="small"
        data-testid="sla-badge"
        data-color={color}
        icon={
          sla.breachOpen ? (
            <WarningAmberIcon data-testid="sla-breach-open" aria-label="Atraso sem justificativa" />
          ) : (
            <ScheduleIcon aria-hidden />
          )
        }
        label={formatMinutes(sla.elapsedMin)}
        sx={{
          height: 22,
          fontSize: 11.5,
          fontWeight: 600,
          bgcolor: alpha(SLA_COLORS[color], 0.15),
          color: TEXT[color],
          "& .MuiChip-icon": { color: TEXT[color], fontSize: 14, ml: "6px" },
        }}
      />
    </Tooltip>
  );
}
